import type { useTranslations } from 'next-intl'

import type { Messages } from '@/lib/i18n/messages'
import {
  getCapabilitiesForProvider,
  type IntegrationCapability,
  type IntegrationCapabilityMeta,
} from '@/lib/integrations/catalog/capabilities'
import type {
  IntegrationMeta,
  IntegrationProvider,
} from '@/lib/integrations/catalog/types'
import { CAPABILITY_MESSAGE_KEY } from '@/lib/integrations/catalog-copy'
import { SUPPORTED_CLERK_EVENTS } from '@/lib/integrations/inbound/providers/clerk/event-types'
import { SUPPORTED_STRIPE_EVENTS } from '@/lib/integrations/oauth/stripe/event-types'

/**
 * The integrations' public words: what `/browse/integrations/**`, their
 * Markdown twins, structured data and OG cards say about each integration,
 * in the `integrationsContent` catalog. Structure (ids, URLs, statuses,
 * event names, counts) stays in the integration catalog
 * (`lib/integrations/catalog`). That catalog keeps its English words for
 * the signed-in app and the machine contracts (`/v1`, MCP);
 * `tests/integrations-copy-catalog.test.ts` pins that the English catalog
 * says exactly the same.
 */

/** `getTranslations('integrationsContent')` (or `useTranslations`). */
export type IntegrationsContentTranslator = ReturnType<
  typeof useTranslations<'integrationsContent'>
>

type ContentMessages = Messages['integrationsContent']
type ContentMessageKey = Parameters<IntegrationsContentTranslator>[0]

/** How many events a provider triggers from, where its copy counts them. */
const EVENT_COUNTS: Partial<Record<IntegrationProvider, number>> = {
  clerk: SUPPORTED_CLERK_EVENTS.length,
  stripe: SUPPORTED_STRIPE_EVENTS.length,
}

function countValues(provider: IntegrationProvider) {
  const count = EVENT_COUNTS[provider]
  return count === undefined ? undefined : { count }
}

/** What an integration does, in the translator's language. */
export function integrationDescription(
  t: IntegrationsContentTranslator,
  provider: IntegrationProvider
): string {
  return t(`providers.${provider}.description`, countValues(provider))
}

/** An integration's one line, in the translator's language. */
export function integrationShortDescription(
  t: IntegrationsContentTranslator,
  provider: IntegrationProvider
): string {
  return t(`providers.${provider}.shortDescription`)
}

/** The Framer guide's steps, in order (`providers.framer.steps`). */
const FRAMER_STEPS = [
  'addForm',
  'addWebhook',
  'matchFields',
  'publish',
  'connectAutomation',
] as const satisfies ReadonlyArray<
  keyof ContentMessages['providers']['framer']['steps']
>

/**
 * `integration` as the public pages show it: its description and one line
 * in the translator's language, and for Framer, whose guide the public page
 * shows, its setup steps.
 */
export function localizeIntegration(
  t: IntegrationsContentTranslator,
  integration: IntegrationMeta
): IntegrationMeta {
  return {
    ...integration,
    description: integrationDescription(t, integration.id),
    shortDescription: integrationShortDescription(t, integration.id),
    ...(integration.id === 'framer'
      ? {
          tutorialSteps: FRAMER_STEPS.map((step) => ({
            label: t(`providers.framer.steps.${step}.label`),
            description: t(`providers.framer.steps.${step}.description`),
          })),
        }
      : {}),
  }
}

/**
 * A capability's label or description: the provider's own words when the
 * catalog has them (`providers.<provider>.capabilities.<key>`), else the
 * capability's.
 */
function capabilityText(
  t: IntegrationsContentTranslator,
  provider: IntegrationProvider,
  capability: IntegrationCapability,
  field: 'label' | 'description'
): string {
  const key = CAPABILITY_MESSAGE_KEY[capability]
  const own =
    `providers.${provider}.capabilities.${key}.${field}` as ContentMessageKey
  return t.has(own)
    ? t(own, countValues(provider))
    : t(`capabilities.${key}.${field}`)
}

/** What `provider` can do, in the translator's language. */
export function getPublicCapabilities(
  t: IntegrationsContentTranslator,
  provider: IntegrationProvider
): Array<IntegrationCapabilityMeta> {
  return getCapabilitiesForProvider(provider).map((capability) => ({
    ...capability,
    label: capabilityText(t, provider, capability.id, 'label'),
    description: capabilityText(t, provider, capability.id, 'description'),
  }))
}

export type IntegrationArticle = {
  sections: Array<{
    heading: string
    paragraphs: Array<string>
    bullets?: Array<string>
  }>
  sources: Array<{ title: string; url: string }>
}
